import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import {
  getPolicyAssignments,
  createPolicyAssignment,
  deletePolicyAssignment,
  getPolicyAssignmentUsers,
  retryPolicyAssignmentForUser,
} from '../../../services/policyService';
import { formatDateTime } from '../../../utils/Utils';
import api from '../../../services/api';
import Button from '../../../components/ui/Button';
import AsyncGenericSelect from '../../../components/ui/AsyncGenericSelect';
import DateTimePicker from '../../../components/ui/DateTimePicker';
import CheckboxInput from '../../../components/ui/CheckboxInput';
import TextInput from '../../../components/ui/TextInput';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';

const Card = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 24px;
  margin-bottom: 16px;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const Title = styled.h3`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #004242;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
`;

const Th = styled.th`
  text-align: left;
  padding: 10px 12px;
  border-bottom: 2px solid #E0E4E8;
  font-weight: 600;
  color: #64748b;
  font-size: 12px;
`;

const Td = styled.td`
  padding: 10px 12px;
  border-bottom: 1px solid #E0E4E8;
  color: #004242;
`;

const DrawerOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  overflow-y: auto;
  padding: 20px 0;
`;

const DrawerContent = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 28px;
  width: 560px;
  max-width: 90vw;
  max-height: 92vh;
  overflow-y: auto;
`;

const FormRow = styled.div`
  margin-bottom: 16px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  cursor: pointer;
  padding: 6px;
  border-radius: 4px;
  color: #ef4444;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  font-weight: 600;
  &:hover { background: #fef2f2; }
`;

const ToggleButton = styled.button`
  background: none;
  border: 1px solid #E0E4E8;
  border-radius: 4px;
  padding: 4px 10px;
  cursor: pointer;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary.main};
  &:hover { background: #f0f9ff; }
`;

const UsersPanel = styled.div`
  background: #F8FAFC;
  padding: 12px 16px;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  background: ${({ $tone }) =>
    $tone === 'success' ? '#DCFCE7' :
    $tone === 'danger' ? '#FEE2E2' :
    $tone === 'warning' ? '#FEF3C7' : '#E0E7FF'};
  color: ${({ $tone }) =>
    $tone === 'success' ? '#166534' :
    $tone === 'danger' ? '#991B1B' :
    $tone === 'warning' ? '#92400E' : '#3730A3'};
`;

const RetryButton = styled.button`
  background: ${({ theme }) => theme.colors.primary.main};
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 4px 10px;
  cursor: pointer;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  &:hover { opacity: 0.9; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

export default function AssignmentsTab({ documentId }) {
  const { t, i18n } = useTranslation();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null });
  const [deleting, setDeleting] = useState(false);
  // Assignment-users expansion: map of assignmentId → { loading, users, retrying: Set<userId> }
  const [expanded, setExpanded] = useState({});

  // Standard multi-target form state
  const [includeAll, setIncludeAll] = useState(false);
  const [autoApplyOnEnter, setAutoApplyOnEnter] = useState(false);
  const [userIds, setUserIds] = useState([]);
  const [departmentIds, setDepartmentIds] = useState([]);
  const [locationIds, setLocationIds] = useState([]);
  const [userGroupIds, setUserGroupIds] = useState([]);

  const [dueDate, setDueDate] = useState(null);
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurrenceMonths, setRecurrenceMonths] = useState('12');

  const fetchAssignments = useCallback(() => {
    setLoading(true);
    getPolicyAssignments({ policyDocumentId: documentId })
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setAssignments(items);
      })
      .finally(() => setLoading(false));
  }, [documentId]);

  useEffect(() => {
    fetchAssignments();
  }, [fetchAssignments]);

  const searchEmployees = useCallback(async (term) => {
    const params = term ? { 'firstName[like]': term, pageSize: 20 } : { pageSize: 20 };
    const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
    return (res.data?.data?.data || []).map((e) => ({
      id: e.id,
      name: `${e.firstName || ''} ${e.lastName || ''}`.trim(),
      profilePicture: e.profilePicture,
    }));
  }, []);

  const searchDepartments = useCallback(async (term) => {
    const params = term ? { 'name[like]': term, pageSize: 20 } : { pageSize: 20 };
    const res = await api.get('/v1/departments', { params });
    return (res.data?.data?.data || []).map((d) => ({ id: d.id, name: d.name }));
  }, []);

  const searchLocations = useCallback(async (term) => {
    const params = term ? { 'name[like]': term, pageSize: 20 } : { pageSize: 20 };
    const res = await api.get('/v1/locations', { params });
    return (res.data?.data?.data || []).map((l) => ({ id: l.id, name: l.name }));
  }, []);

  const searchUserGroups = useCallback(async (term) => {
    const params = term ? { 'name[like]': term, pageSize: 20 } : { pageSize: 20 };
    const res = await api.get('/v1/user-groups', { params });
    return (res.data?.data?.data || []).map((g) => ({ id: g.id, name: g.name }));
  }, []);

  const resetForm = () => {
    setIncludeAll(false);
    setAutoApplyOnEnter(false);
    setUserIds([]);
    setDepartmentIds([]);
    setLocationIds([]);
    setUserGroupIds([]);
    setDueDate(null);
    setIsRecurring(false);
    setRecurrenceMonths('12');
  };

  const handleOpenDrawer = useCallback(() => {
    resetForm();
    setDrawerOpen(true);
  }, []);

  const hasTarget = includeAll
    || userIds.length > 0
    || departmentIds.length > 0
    || locationIds.length > 0
    || userGroupIds.length > 0;

  const canSave = hasTarget && !!dueDate && !saving;

  const handleSave = useCallback(async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await createPolicyAssignment({
        policyDocumentId: documentId,
        includeAll,
        userIds: includeAll ? [] : userIds,
        departmentIds: includeAll ? [] : departmentIds,
        locationIds: includeAll ? [] : locationIds,
        userGroupIds: includeAll ? [] : userGroupIds,
        autoApplyOnEnter,
        dueDate,
        isRecurring,
        recurrenceMonths: isRecurring ? Number(recurrenceMonths) || 12 : undefined,
      });
      setDrawerOpen(false);
      fetchAssignments();
    } catch {
      // error handled by interceptor
    } finally {
      setSaving(false);
    }
  }, [canSave, documentId, includeAll, autoApplyOnEnter, userIds, departmentIds, locationIds, userGroupIds,
      dueDate, isRecurring, recurrenceMonths, fetchAssignments]);

  const handleDelete = useCallback(async () => {
    setDeleting(true);
    try {
      await deletePolicyAssignment(deleteConfirm.id);
      setDeleteConfirm({ isOpen: false, id: null });
      fetchAssignments();
    } catch {
      // error handled by interceptor
    } finally {
      setDeleting(false);
    }
  }, [deleteConfirm.id, fetchAssignments]);

  const assignmentTypeLabels = useMemo(() => ({
    0: t('policy.assignAll'),
    1: t('policy.assignDepartment'),
    2: t('policy.assignLocation'),
    3: t('policy.assignUserGroup'),
    4: t('policy.assignIndividual'),
  }), [t]);

  const taskStatusMeta = useMemo(() => ({
    0: { label: t('policy.taskStatus.pending'),        tone: 'info' },
    1: { label: t('policy.taskStatus.documentOpened'), tone: 'info' },
    2: { label: t('policy.taskStatus.quizStarted'),    tone: 'warning' },
    3: { label: t('policy.taskStatus.completed'),      tone: 'success' },
    4: { label: t('policy.taskStatus.overdue'),        tone: 'danger' },
  }), [t]);

  const loadUsers = useCallback(async (assignmentId) => {
    setExpanded((prev) => ({
      ...prev,
      [assignmentId]: { ...(prev[assignmentId] || {}), loading: true, users: prev[assignmentId]?.users || [], retrying: prev[assignmentId]?.retrying || {} },
    }));
    try {
      const res = await getPolicyAssignmentUsers(assignmentId, { pageNumber: 1, pageSize: 200 });
      const list = res.data?.data?.data || [];
      setExpanded((prev) => ({
        ...prev,
        [assignmentId]: { loading: false, users: list, retrying: prev[assignmentId]?.retrying || {} },
      }));
    } catch {
      setExpanded((prev) => ({
        ...prev,
        [assignmentId]: { loading: false, users: [], retrying: {} },
      }));
    }
  }, []);

  const toggleExpand = useCallback((assignmentId) => {
    setExpanded((prev) => {
      // Toggle: if present, remove; else trigger load below.
      if (prev[assignmentId]) {
        const { [assignmentId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [assignmentId]: { loading: true, users: [], retrying: {} } };
    });
    // Fetch only when opening (expanded state didn't previously exist)
    if (!expanded[assignmentId]) loadUsers(assignmentId);
  }, [expanded, loadUsers]);

  const handleRetry = useCallback(async (assignmentId, userId) => {
    setExpanded((prev) => ({
      ...prev,
      [assignmentId]: {
        ...prev[assignmentId],
        retrying: { ...(prev[assignmentId]?.retrying || {}), [userId]: true },
      },
    }));
    try {
      await retryPolicyAssignmentForUser(assignmentId, userId);
      await loadUsers(assignmentId);
    } catch {
      setExpanded((prev) => ({
        ...prev,
        [assignmentId]: {
          ...prev[assignmentId],
          retrying: { ...(prev[assignmentId]?.retrying || {}), [userId]: false },
        },
      }));
    }
  }, [loadUsers]);

  return (
    <div>
      <Card>
        <Header>
          <Title>{t('policy.assignments')}</Title>
          <Button onClick={handleOpenDrawer}>
            {t('policy.newAssignment')}
          </Button>
        </Header>

        <Table>
          <thead>
            <tr>
              <Th>{t('policy.assignmentType')}</Th>
              <Th>{t('policy.assignmentTarget')}</Th>
              <Th>{t('policy.dueDate')}</Th>
              <Th>{t('policy.recurring')}</Th>
              <Th>{t('policy.createDate')}</Th>
              <Th />
              <Th />
            </tr>
          </thead>
          <tbody>
            {assignments.length === 0 && !loading && (
              <tr><Td colSpan={7} style={{ textAlign: 'center', color: '#A0A8B4' }}>{t('common.noRecords')}</Td></tr>
            )}
            {assignments.map((a) => {
              const expansion = expanded[a.id];
              const isOpen = !!expansion;
              return (
                <React.Fragment key={a.id}>
                  <tr>
                    <Td>{assignmentTypeLabels[a.assignmentType] || a.assignmentType}</Td>
                    <Td>{a.targetName || (a.assignmentType === 0 ? t('policy.assignAll') : '—')}</Td>
                    <Td>{formatDateTime(a.dueDate, i18n.language)}</Td>
                    <Td>{a.isRecurring ? `${t('common.yes')} (${a.recurrenceMonths} ${t('policy.months')})` : t('common.no')}</Td>
                    <Td>{formatDateTime(a.createDate, i18n.language)}</Td>
                    <Td>
                      <ToggleButton onClick={() => toggleExpand(a.id)}>
                        {isOpen ? t('policy.hideUsers') : t('policy.showUsers')}
                      </ToggleButton>
                    </Td>
                    <Td>
                      <IconButton onClick={() => setDeleteConfirm({ isOpen: true, id: a.id })}>
                        {t('common.delete')}
                      </IconButton>
                    </Td>
                  </tr>
                  {isOpen && (
                    <tr>
                      <Td colSpan={7} style={{ padding: 0 }}>
                        <UsersPanel>
                          {expansion.loading ? (
                            <div style={{ color: '#64748b', fontSize: 13 }}>{t('common.loading')}</div>
                          ) : expansion.users.length === 0 ? (
                            <div style={{ color: '#A0A8B4', fontSize: 13 }}>{t('common.noRecords')}</div>
                          ) : (
                            <Table>
                              <thead>
                                <tr>
                                  <Th>{t('policy.userName')}</Th>
                                  <Th>{t('policy.taskStatusColumn')}</Th>
                                  <Th>{t('policy.quizProgress')}</Th>
                                  <Th>{t('policy.lastScore')}</Th>
                                  <Th />
                                </tr>
                              </thead>
                              <tbody>
                                {expansion.users.map((u) => {
                                  const meta = taskStatusMeta[u.status] || taskStatusMeta[0];
                                  const quizTone = u.quizPassed ? 'success' : (u.canRetry ? 'danger' : 'info');
                                  return (
                                    <tr key={u.userId}>
                                      <Td>{u.fullName}</Td>
                                      <Td><StatusBadge $tone={meta.tone}>{meta.label}</StatusBadge></Td>
                                      <Td>
                                        {u.hasQuiz ? (
                                          <>
                                            <StatusBadge $tone={quizTone}>
                                              {u.quizPassed
                                                ? t('policy.quizPassed')
                                                : u.canRetry
                                                  ? t('policy.quizFailedExhausted')
                                                  : t('policy.quizPending')}
                                            </StatusBadge>
                                            <span style={{ fontSize: 12, color: '#64748b', marginLeft: 8 }}>
                                              {u.attemptCount}/{u.maxAttempts}
                                            </span>
                                          </>
                                        ) : (
                                          <span style={{ color: '#A0A8B4' }}>—</span>
                                        )}
                                      </Td>
                                      <Td>{u.lastScorePercentage != null ? `%${u.lastScorePercentage}` : '—'}</Td>
                                      <Td>
                                        {u.canRetry && (
                                          <RetryButton
                                            disabled={!!expansion.retrying?.[u.userId]}
                                            onClick={() => handleRetry(a.id, u.userId)}
                                          >
                                            {expansion.retrying?.[u.userId] ? '...' : t('policy.retryAssignment')}
                                          </RetryButton>
                                        )}
                                      </Td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </Table>
                          )}
                        </UsersPanel>
                      </Td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </Table>
      </Card>

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title={t('policy.deleteAssignmentTitle')}
        message={t('policy.deleteAssignmentMessage')}
        onConfirm={handleDelete}
        onCancel={() => setDeleteConfirm({ isOpen: false, id: null })}
        loading={deleting}
      />

      {drawerOpen && (
        <DrawerOverlay onClick={() => !saving && setDrawerOpen(false)}>
          <DrawerContent onClick={(e) => e.stopPropagation()}>
            <h3 style={{ fontFamily: "'Nunito Sans', sans-serif", fontSize: 16, fontWeight: 700, marginBottom: 20, color: '#004242' }}>
              {t('policy.newAssignment')}
            </h3>

            <FormRow>
              <CheckboxInput
                label={t('policy.assignAll')}
                checked={includeAll}
                onChange={setIncludeAll}
              />
            </FormRow>

            <FormRow>
              <CheckboxInput
                label={t('assignment.autoApplyOnEnter')}
                checked={autoApplyOnEnter}
                onChange={setAutoApplyOnEnter}
              />
              <small style={{ fontSize: 11, color: '#A0A8B4', marginTop: 4, display: 'block' }}>
                {t('assignment.autoApplyOnEnterHint')}
              </small>
            </FormRow>

            {!includeAll && (
              <>
                <FormRow>
                  <AsyncGenericSelect
                    label={t('policy.assignByEmployee')}
                    value={userIds}
                    onChange={setUserIds}
                    onSearch={searchEmployees}
                    isMulti
                  />
                </FormRow>
                <FormRow>
                  <AsyncGenericSelect
                    label={t('policy.assignByDepartment')}
                    value={departmentIds}
                    onChange={setDepartmentIds}
                    onSearch={searchDepartments}
                    isMulti
                  />
                </FormRow>
                <FormRow>
                  <AsyncGenericSelect
                    label={t('policy.assignByLocation')}
                    value={locationIds}
                    onChange={setLocationIds}
                    onSearch={searchLocations}
                    isMulti
                  />
                </FormRow>
                <FormRow>
                  <AsyncGenericSelect
                    label={t('policy.assignByUserGroup')}
                    value={userGroupIds}
                    onChange={setUserGroupIds}
                    onSearch={searchUserGroups}
                    isMulti
                  />
                </FormRow>
              </>
            )}

            <FormRow>
              <DateTimePicker
                label={t('policy.dueDate')}
                value={dueDate}
                onChange={setDueDate}
                required
              />
            </FormRow>

            <FormRow>
              <CheckboxInput
                label={t('policy.recurring')}
                checked={isRecurring}
                onChange={setIsRecurring}
              />
            </FormRow>

            {isRecurring && (
              <FormRow>
                <TextInput
                  label={t('policy.recurrenceMonths')}
                  value={recurrenceMonths}
                  onChange={(e) => setRecurrenceMonths(e.target.value)}
                  type="number"
                />
              </FormRow>
            )}

            <Footer>
              <Button variant="ghost" onClick={() => setDrawerOpen(false)} disabled={saving}>
                {t('common.cancel')}
              </Button>
              <Button onClick={handleSave} disabled={!canSave}>
                {saving ? '...' : t('common.save')}
              </Button>
            </Footer>
          </DrawerContent>
        </DrawerOverlay>
      )}
    </div>
  );
}
